import React, { useEffect, useRef, useState } from "react";
import { RiGeminiFill, RiBookOpenLine, RiCloseLine, RiPlanetLine, RiLightbulbLine } from "@remixicon/react";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import Loader from "@/components/ui/loader";
import {
  whatsNewEntries,
  getRelativeLabel,
  groupEntriesByDate,
} from "./WhatsNewContent";

const SEEN_KEY = "whats-new-seen-ids";
const PREVIEW_DISMISSED_KEY = "whats-new-preview-dismissed";
const NOTIFY_PREF_KEY = "whats-new-notify-on-update";

function getSeenIds(): Set<string> {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function markAllSeen(entries: typeof whatsNewEntries) {
  const ids = entries.map((e) => e.id);
  localStorage.setItem(SEEN_KEY, JSON.stringify(ids));
}

export function hasUnseenEntries(): boolean {
  const seen = getSeenIds();
  return whatsNewEntries.some((e) => !seen.has(e.id));
}

function getLatestEntryId(): string | null {
  if (whatsNewEntries.length === 0) return null;
  const sorted = [...whatsNewEntries].sort((a, b) => b.date.localeCompare(a.date));
  return sorted[0].id;
}

export function isPreviewDismissed(): boolean {
  const dismissed = localStorage.getItem(PREVIEW_DISMISSED_KEY);
  return dismissed === getLatestEntryId();
}

function dismissPreview() {
  const latestId = getLatestEntryId();
  if (latestId) localStorage.setItem(PREVIEW_DISMISSED_KEY, latestId);
}

export function getNotifyOnUpdate(): boolean {
  const val = localStorage.getItem(NOTIFY_PREF_KEY);
  return val === null ? true : val === "true";
}

function setNotifyOnUpdate(enabled: boolean) {
  localStorage.setItem(NOTIFY_PREF_KEY, String(enabled));
}

export function shouldShowPreview(): boolean {
  return getNotifyOnUpdate() && hasUnseenEntries() && !isPreviewDismissed();
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function renderFormattedText(text: string): React.ReactNode {
  const paragraphs = text.split("\n\n");
  return paragraphs.map((para, pIdx) => {
    const lines = para.split("\n");
    const listItems = lines.filter((l) => /^\d+\.\s/.test(l.trim()));
    if (listItems.length > 0) {
      const nonListLines = lines.filter((l) => !/^\d+\.\s/.test(l.trim()));
      return (
        <React.Fragment key={pIdx}>
          {nonListLines.length > 0 && (
            <span>{renderInlineBold(nonListLines.join(" "))}</span>
          )}
          <ol className="list-decimal pl-4 mt-1 flex flex-col gap-0.5">
            {listItems.map((item, lIdx) => (
              <li key={lIdx}>
                {renderInlineBold(item.replace(/^\d+\.\s*/, ""))}
              </li>
            ))}
          </ol>
        </React.Fragment>
      );
    }
    return (
      <span key={pIdx} className={pIdx > 0 ? "mt-2 block" : ""}>
        {renderInlineBold(para.replace(/\n/g, " "))}
      </span>
    );
  });
}

function renderInlineBold(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <span key={i} className="font-medium">{part.slice(2, -2)}</span>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

// --- Preview Component ---

interface WhatsNewPreviewProps {
  open: boolean;
  onClose: () => void;
  onOpenWhatsNew: () => void;
}

export function WhatsNewPreview({ open, onClose, onOpenWhatsNew }: WhatsNewPreviewProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [notifyChecked, setNotifyChecked] = useState(() => getNotifyOnUpdate());

  useEffect(() => {
    if (!open) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        dismissPreview();
        onClose();
      }
    };
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("keydown", handleEsc);
    };
  }, [open, onClose]);

  if (!open || whatsNewEntries.length === 0) return null;

  const sorted = [...whatsNewEntries].sort((a, b) => b.date.localeCompare(a.date));
  const latestEntry = sorted[0];

  return (
    <div
      ref={panelRef}
      className={cn(
        "fixed z-[100] bg-white rounded-[12px] border border-border-gray",
        "w-[380px] flex flex-col",
        "left-[48px] bottom-[72px]",
        "shadow-[0_0_20px_0_rgba(0,0,0,0.12)]",
        "animate-in fade-in-0 slide-in-from-left-2 duration-200",
      )}
    >
      <div className="p-4 flex flex-col gap-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg width="0" height="0" style={{ position: "absolute" }}>
              <defs>
                <linearGradient id="preview-gemini-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#FF4500" />
                  <stop offset="100%" stopColor="#FF4699" />
                </linearGradient>
              </defs>
            </svg>
            <RiGeminiFill size={18} style={{ fill: "url(#preview-gemini-gradient)" }} />
            <h2 className="text-[14px] font-medium text-[#35467A]">What's new</h2>
          </div>
          <button
            onClick={() => {
              dismissPreview();
              onClose();
            }}
            className="text-[#9AA3BD] hover:text-[#67749B] transition-colors"
          >
            <RiCloseLine size={22} />
          </button>
        </div>

        {/* Latest Card */}
        <div className="rounded-[8px] border border-[#E7E8E9] overflow-hidden bg-white">
          <div className="w-full bg-[#F4F4F5]">
            <img
              src={latestEntry.image}
              alt={latestEntry.title}
              className="w-full h-auto object-contain"
            />
          </div>
          <div className="px-[14px] py-3 flex flex-col gap-1.5">
            <h3 className="text-[13px] font-medium text-[#021859]/80 leading-[19.16px] text-ellipsis overflow-hidden">
              {latestEntry.title}
            </h3>
            <div className="text-[12px] font-normal leading-[16px] text-[#021859]/60 text-ellipsis overflow-hidden">
              {renderFormattedText(latestEntry.description)}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-end justify-between">
          <label className="flex items-center gap-2 cursor-pointer select-none pb-[6px]">
            <Checkbox
              checked={notifyChecked}
              onCheckedChange={(checked) => {
                const val = checked === true;
                setNotifyOnUpdate(val);
                setNotifyChecked(val);
              }}
            />
            <span className="text-[12px] font-normal text-[#021859]/80">Notify on update</span>
          </label>
          <Button
            variant="outline"
            onClick={onOpenWhatsNew}
            className="text-[12px] rounded-[8px] border border-border-gray bg-white flex items-center gap-[6px] py-[6px] px-[14px]"
          >
            <RiBookOpenLine size={14} className="text-[#9AA3BD]" />
            <span className="overflow-hidden text-brand-primary-800 text-ellipsis text-[12px] font-medium leading-[19.157px] tracking-[0.24px] pt-[2px]">
              See all
            </span>
          </Button>
        </div>
      </div>
    </div>
  );
}

// --- Full Panel Component ---

interface WhatsNewProps {
  open: boolean;
  onClose: () => void;
}

export default function WhatsNew({ open, onClose }: WhatsNewProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [notifyChecked, setNotifyChecked] = useState(() => getNotifyOnUpdate());
  const [imagesLoaded, setImagesLoaded] = useState(false);

  useEffect(() => {
    if (!open) {
      setImagesLoaded(false);
      return;
    }

    markAllSeen(whatsNewEntries);
    dismissPreview();
    setNotifyChecked(getNotifyOnUpdate());

    const imageUrls = whatsNewEntries.map((e) => e.image);
    if (imageUrls.length === 0) {
      setImagesLoaded(true);
      return;
    }

    let cancelled = false;
    Promise.all(
      imageUrls.map(
        (src) =>
          new Promise<void>((resolve) => {
            const img = new Image();
            img.onload = () => resolve();
            img.onerror = () => resolve();
            img.src = src;
          }),
      ),
    ).then(() => {
      if (!cancelled) setImagesLoaded(true);
    });

    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [open, onClose]);

  if (!open) return null;

  const grouped = groupEntriesByDate(whatsNewEntries);
  const mostRecentDate = grouped.length > 0 ? grouped[0].date : null;

  return (
    <div
      ref={panelRef}
      className={cn(
        "fixed z-[100] bg-white rounded-[12px] border border-border-gray shadow-xl",
        "w-[380px] flex flex-col",
        "left-[48px] top-1 bottom-1",
        "animate-in fade-in-0 slide-in-from-left-2 duration-200",
      )}
    >
      {/* Header */}
      <div className="px-5 pt-5 pb-3 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg width="0" height="0" style={{ position: "absolute" }}>
              <defs>
                <linearGradient id="panel-gemini-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#FF4500" />
                  <stop offset="100%" stopColor="#FF4699" />
                </linearGradient>
              </defs>
            </svg>
            <RiGeminiFill size={18} style={{ fill: "url(#panel-gemini-gradient)" }} />
            <h2 className="text-[14px] font-medium text-[#35467A]">What's new</h2>
          </div>
          <Button
            variant="outline"
            asChild
            className="text-[12px] rounded-[8px] border border-border-gray bg-white flex items-center gap-[6px] py-[6px] px-[14px]"
          >
            <a href="https://forms.gle/emJYo8RpRrGwv1Su6" target="_blank" rel="noopener noreferrer">
              <RiLightbulbLine size={14} className="text-[#9AA3BD]" />
              <span className="overflow-hidden text-brand-primary-800 text-ellipsis text-[12px] font-medium leading-[19.157px] tracking-[0.24px] pt-[2px]">
                Request feature
              </span>
            </a>
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="relative flex-1 min-h-0">
        {!imagesLoaded ? (
          <div className="flex items-center justify-center h-full">
            <Loader />
          </div>
        ) : (
        <>
        <div className="h-full overflow-y-auto">
        <div className="px-4 pt-6 pb-16 flex flex-col gap-5">
          {grouped.map((group) => {
            const isMostRecent = group.date === mostRecentDate;
            const label = isMostRecent
              ? getRelativeLabel(group.date)
              : formatDate(group.date);
            const iconColor = isMostRecent ? "#FF8F66" : "#67749B";
            const textColor = isMostRecent ? "#F04438" : "#35467A";
            const pillBg = isMostRecent ? "bg-[#FEF3F2]" : "bg-[#F7F8FA]";
            const pillBorder = isMostRecent ? "border-[#F04438]/20" : "border-[#CCD1DE]";

            return (
              <div key={group.date} className="flex flex-col gap-4">
                <span
                  className={cn(
                    "inline-flex items-center gap-1 w-fit h-[24px] px-2 rounded-[6px] border text-[11px] font-normal leading-none",
                    pillBorder,
                    pillBg,
                  )}
                  style={{ color: textColor }}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <circle cx="6" cy="6" r="5" stroke={iconColor} strokeWidth="1.2" />
                    <path
                      d="M6 3.5V6L7.5 7.5"
                      stroke={iconColor}
                      strokeWidth="1.2"
                      strokeLinecap="round"
                    />
                  </svg>
                  <span className="pt-[2px]">{label}</span>
                </span>
                {group.entries.map((entry) => (
                  <div
                    key={entry.id}
                    className="rounded-[8px] border border-[#E7E8E9] overflow-hidden bg-white"
                  >
                    <div className="w-full bg-[#F4F4F5]">
                      <img
                        src={entry.image}
                        alt={entry.title}
                        className="w-full h-auto object-contain"
                      />
                    </div>
                    <div className="px-[14px] py-3 flex flex-col gap-1.5">
                      <h3 className="text-[13px] font-medium text-[#021859]/80 leading-[19.16px] text-ellipsis overflow-hidden">
                        {entry.title}
                      </h3>
                      <div className="text-[12px] font-normal leading-[16px] text-[#021859]/60 text-ellipsis overflow-hidden">
                        {renderFormattedText(entry.description)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
          <div className="flex justify-center pb-0 pt-4">
            <Button
              variant="ghost"
              className="text-[12px] bg-transparent border-none shadow-none flex items-center gap-[10px] py-[6px] px-[14px] hover:bg-transparent"
            >
              <RiPlanetLine
                size={14}
                className="text-[#9AA3BD] animate-orbit"
              />
              <span className="overflow-hidden text-[#9AA3BD] text-ellipsis text-[12px] font-medium leading-[19.157px] tracking-[0.24px] pt-[2px]">
                Big Bang!
              </span>
            </Button>
          </div>
        </div>
        </div>
        {/* Fade overlay */}
        <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-[80px] bg-gradient-to-t from-white to-transparent rounded-b-[12px]" />
        </>
        )}
      </div>

      {/* Footer */}
      <div className="shrink-0 px-4 pb-4 pt-2">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <Checkbox
            checked={notifyChecked}
            onCheckedChange={(checked) => {
              const val = checked === true;
              setNotifyOnUpdate(val);
              setNotifyChecked(val);
            }}
          />
          <span className="text-[12px] font-normal text-[#021859]/80">Notify on update</span>
        </label>
      </div>
    </div>
  );
}
